import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "wouter";
import {
  CLIENT_REPORT_STATUS_MEANINGS,
  VERIFICATION_REVOCATION_REASONS,
  canTransitionVerification,
  type VerificationRevocationReason,
} from "@titleverify/shared";
import {
  apiErrorText,
  apiFetch,
  type Case,
  type ProfessionalProfile,
  type ReportCounts,
  type ReportCountsResponse,
} from "../api.js";
import { useAuth } from "../auth.js";
 import { AdminBreadcrumb } from "./AdminHome.js";

/**
 * THE ROSTER — every professional, their standing, and the reports beside them.
 *
 * ===========================================================================
 * READ THIS BEFORE CHANGING A WORD OF COPY ON THIS PAGE.
 * ===========================================================================
 *
 * This screen shows counts of complaints next to named attorneys. It is the
 * single most dangerous surface in the product, and everything about it is built
 * to prevent one specific misreading: that the platform has decided somebody
 * behaved improperly.
 *
 * It has not, and it may not. In Sri Lanka an attorney-at-law's conduct is for
 * the Bar Association of Sri Lanka and the Supreme Court, which alone may
 * disenrol. A platform asserting misconduct is publishing a defamatory statement
 * of fact it cannot prove, and is doing the conduct equivalent of rendering a
 * legal conclusion — which CLAUDE.md non-negotiable 2 forbids in the title domain
 * for exactly the same reason.
 *
 * So the two rules this page is built around:
 *
 *   1. THE NUMBER NEVER STANDS ALONE. Every count renders with what it counts,
 *      in wording that comes from CLIENT_REPORT_STATUS_MEANINGS rather than from
 *      anything written here. "3" beside a name, unqualified, is read as a
 *      misconduct tally by every human who has ever seen a review site.
 *   2. NOTHING ON THIS PAGE HAPPENS AUTOMATICALLY. There is no threshold, no
 *      sort by complaint count, no highlighting of the worst offenders. Removing
 *      someone by arithmetic would be a conduct determination that nobody signed
 *      and nobody can be asked about — worse than one written in prose.
 *
 * This is internal ops, not a directory: an admin sees it in order to decide
 * where to send work, which is the opposite of advertising professionals to
 * clients to attract work for them (COMPLIANCE Constraint 1). The server refuses
 * `professional_profile:list` and `client_report:list` to every role but admin.
 */

/** Statuses at which a case is over and no longer needs anybody working it. */
const FINISHED: readonly string[] = ["CLOSED", "RETENTION_EXPIRED"];

const NO_COUNTS: Omit<ReportCounts, "aboutUserId"> = {
  upheldCount: 0,
  openCount: 0,
  dismissedCount: 0,
};

export function AdminRoster() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  // No status filter — the roster is everyone. A verified professional and a
  // revoked one have to be readable in the same list, or "who are we routing to"
  // is a question this screen cannot answer.
  const roster = useQuery({
    queryKey: ["admin", "professionals", "all"],
    queryFn: async () =>
      apiFetch<{ professionals: ProfessionalProfile[] }>("/admin/professionals", {
        token: await getToken(),
      }),
  });

  const counts = useQuery({
    queryKey: ["admin", "reports", "counts"],
    queryFn: async () =>
      apiFetch<ReportCountsResponse>("/admin/reports/counts", { token: await getToken() }),
  });

  // Admin scope on /cases is every case (workflow/views.ts). Read here so the
  // revocation panel can state a real consequence instead of a general warning.
  const allCases = useQuery({
    queryKey: ["admin", "cases"],
    queryFn: async () => apiFetch<{ cases: Case[] }>("/cases", { token: await getToken() }),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["admin", "professionals"] });
    void queryClient.invalidateQueries({ queryKey: ["admin", "cases"] });
  };

  if (roster.isPending || counts.isPending || allCases.isPending) {
    return <p className="muted" role="status">Loading the roster…</p>;
  }

  // Each failure is named. A roster rendered from two of three sources would be
  // a roster missing counts with no sign that anything was missing, which is the
  // fabricated-completeness failure CLAUDE.md non-negotiable 1 is about.
  if (roster.error) {
    return <p role="alert">{apiErrorText(roster.error, "Could not load the roster.")}</p>;
  }
  if (counts.error) {
    return (
      <p role="alert">
        {apiErrorText(counts.error, "Loaded the professionals, but not the report counts.")} Not
        showing the roster without them — a roster with silently missing counts reads as a
        roster with no reports.
      </p>
    );
  }
  if (allCases.error) {
    return (
      <p role="alert">
        {apiErrorText(allCases.error, "Loaded the professionals, but not the cases.")} Not
        showing the roster without them — revoking someone here has to state how many live
        cases they are on, and that number would be missing.
      </p>
    );
  }

  const countsByUser = new Map(counts.data.counts.map((c) => [c.aboutUserId, c]));
  const professionals = roster.data.professionals;

  return (
    <section className="wide">
      <AdminBreadcrumb />
      <header className="page-head">
        <p className="eyebrow">Who work is routed to</p>
        <h2>Professional roster</h2>
        {professionals.length > 0 ? (
          <p className="lede">
            {professionals.length} {professionals.length === 1 ? "professional" : "professionals"}.
            Only a <strong>verified</strong> one can be assigned to a case.
          </p>
        ) : null}
      </header>

      {professionals.length === 0 ? (
        <p className="empty">
          No professional has an account yet. This is an honest empty roster, not a failure to
          load — professionals appear here when they open an account, before anything about them
          has been checked.
        </p>
      ) : (
        <>
          {/*
            The server's own caveat about its numbers, rendered as it sent it.
            Paraphrasing it here would be a second copy of the most carefully
            worded sentence in the system, free to drift from the first.
          */}
          <p className="caveat">{counts.data.note}</p>

          <div className="table-wrap" tabIndex={0} role="region" aria-label="Professional roster">
            <table>
              <thead>
                <tr>
                  <th>Enrolment / registration</th>
                  <th>Claims to be</th>
                  <th>Standing</th>
                  <th>Reports</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {professionals.map((profile) => (
                  <RosterRow
                    key={profile.id}
                    profile={profile}
                    counts={countsByUser.get(profile.userId) ?? { aboutUserId: profile.userId, ...NO_COUNTS }}
                    liveCases={allCases.data.cases.filter(
                      (c) => c.assignedProfessionalId === profile.userId && !FINISHED.includes(c.status),
                    )}
                    onChanged={invalidate}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/*
            An honest note about what this screen cannot show, rather than a
            column of blanks. The admin professional payload carries no name and
            no email — `present()` in professionals/routes.ts sends the profile
            fields only. The enrolment number is what an admin verified against a
            source, so it is the identifier this roster leads with; it is not,
            however, what an operator has in their head when they think of a
            person. Saying so is better than a "Name" column full of dashes.
          */}
          <p className="muted">
            This roster identifies professionals by the enrolment or registration number that was
            checked, because that is the only identifier the API returns for them. Names and
            contact details are not shown here.
          </p>
        </>
      )}
    </section>
  );
}

function RosterRow({
  profile,
  counts,
  liveCases,
  onChanged,
}: {
  profile: ProfessionalProfile;
  counts: ReportCounts;
  liveCases: Case[];
  onChanged: () => void;
}) {
  const [confirming, setConfirming] = useState(false);

  // Mirrors the server rule rather than inventing one: only a verified profile
  // can be revoked. The server refuses anything else regardless.
  const canRevoke = canTransitionVerification(profile.verificationStatus, "revoked");

  return (
    <>
      <tr>
        <td>
          {/*
            The identifier, not a name — see the API-gap note in the page footer.
            It is also the honest one to lead with: the enrolment number is the
            thing an admin actually verified against a source.
          */}
          {profile.enrolmentNo ?? profile.notaryRegistrationNo ?? (
            <span className="muted">none given</span>
          )}
        </td>
        <td>{profile.type}</td>
        <td>
          <span className={`status status-${profile.verificationStatus}`}>
            {profile.verificationStatus}
          </span>
          <br />
          <small>{profile.assignable ? "Can be assigned work" : "Cannot be assigned work"}</small>
        </td>
        <td>
          <ReportTally counts={counts} aboutUserId={profile.userId} />
        </td>
        <td>
          {canRevoke ? (
            <button
              type="button"
              className="quiet compact"
              onClick={() => setConfirming((c) => !c)}
            >
              {confirming ? "Cancel" : "Stop routing work"}
            </button>
          ) : (
            <small className="muted">
              {profile.verificationStatus === "revoked"
                ? "Already not being routed work"
                : "Not verified, so already unassignable"}
            </small>
          )}
        </td>
      </tr>
      {confirming ? (
        <tr>
          <td colSpan={5}>
            <RevokePanel
              profile={profile}
              liveCases={liveCases}
              onDone={() => {
                setConfirming(false);
                onChanged();
              }}
            />
          </td>
        </tr>
      ) : null}
    </>
  );
}

/**
 * The counts, each one carrying what it counts.
 *
 * All three are always rendered, including the zeroes. Showing only the non-zero
 * ones would make "3 upheld" appear beside a name with no indication that four
 * more reports are sitting unread — pending is pending, not absence (CLAUDE.md
 * non-negotiable 1), and the shape of the API response was chosen to stop a UI
 * fetching only the flattering number. Dropping the zeroes here would undo that.
 */
function ReportTally({ counts, aboutUserId }: { counts: ReportCounts; aboutUserId: string }) {
  const total = counts.openCount + counts.upheldCount + counts.dismissedCount;

  if (total === 0) {
    return <small className="muted">No reports filed.</small>;
  }

  return (
    <>
      <ul className="tally">
        <li>
          <strong>{counts.openCount}</strong> awaiting review{" "}
          <small>{CLIENT_REPORT_STATUS_MEANINGS.open}</small>
        </li>
        <li>
          <strong>{counts.upheldCount}</strong> the platform is taking into account{" "}
          <small>{CLIENT_REPORT_STATUS_MEANINGS.upheld}</small>
        </li>
        <li>
          <strong>{counts.dismissedCount}</strong> the platform is not taking into account{" "}
          <small>{CLIENT_REPORT_STATUS_MEANINGS.dismissed}</small>
        </li>
      </ul>
      <Link href={`/admin/reports?aboutUserId=${aboutUserId}`}>Read the reports themselves</Link>
    </>
  );
}

/**
 * REMOVAL. Read this before touching it.
 *
 * There is exactly one removal mechanism and this is not a new one — it is the
 * existing admin act of revoking verification, which makes the professional
 * unassignable here and touches nothing else. No row is deleted: the reports are
 * the evidence for a decision the platform made, and users rows are deliberately
 * undeletable.
 *
 * The consequence is shown BEFORE the confirm, and it is the real one rather
 * than the one you would assume. See the copy below — this was checked against
 * the migrations rather than inferred, and what actually happens is not what a
 * reasonable person expects.
 */
function RevokePanel({
  profile,
  liveCases,
  onDone,
}: {
  profile: ProfessionalProfile;
  liveCases: Case[];
  onDone: () => void;
}) {
  const { getToken } = useAuth();
  const [reason, setReason] = useState<VerificationRevocationReason>("WITHDRAWN_BY_PLATFORM");

  const revoke = useMutation({
    mutationFn: async () =>
      apiFetch<ProfessionalProfile>(`/admin/professionals/${profile.id}/decision`, {
        token: await getToken(),
        method: "POST",
        body: { decision: "revoke", reason },
      }),
    onSuccess: onDone,
  });

  return (
    <div className="consequence">
      <h3>Stop routing work to this professional</h3>

      <p>
        <strong>What this does.</strong> Their verification leaves <em>verified</em>, so no
        further case on this platform can be assigned to them. That is the whole of it.
      </p>

      <p>
        <strong>What this is not.</strong> It says nothing about their enrolment as an
        attorney-at-law, which is not ours to touch and is unaffected. It is not a finding that
        they did anything wrong, and it is not a sanction. Whether an attorney has committed
        professional misconduct is for the Bar Association of Sri Lanka and the Supreme Court,
        which alone may disenrol. This is only a commercial decision about where this platform
        sends its own work.
      </p>

      {/*
        THE CONSEQUENCE, AND IT IS NOT THE OBVIOUS ONE.
        =================================================================
        Revoking VERIFICATION does not revoke their ASSIGNMENTS. Migration 0010
        §3 checks verification only when the case pointer is SET, deliberately —
        "if an attorney's enrolment is struck off mid-case, their live assignment
        row still stands and the case is not frozen". And tv_verify_professional
        (0018) writes to professional_profiles and nothing else.

        So the cascade in 0019 §3 that returns a case to ROUTED fires when an
        ASSIGNMENT is revoked, which happens on reassignment — not here. Live
        cases stay assigned, and the professional keeps access to them, until an
        admin reassigns each one by hand.

        That is a nastier surprise than the reverse would have been, which is
        exactly why it is stated in full and in numbers.
      */}
      {liveCases.length === 0 ? (
        <p>
          <strong>Live cases: none.</strong> They are not currently assigned to any case that is
          still running, so nothing is left mid-flight.
        </p>
      ) : (
        <div role="alert">
          <p>
            <strong>
              They are on {liveCases.length} live{" "}
              {liveCases.length === 1 ? "case" : "cases"}, and this does not take them off{" "}
              {liveCases.length === 1 ? "it" : "them"}.
            </strong>
          </p>
          <p>
            Revoking verification stops <em>new</em> assignments. It does not end the ones already
            made: {liveCases.length === 1 ? "this case stays" : "these cases stay"} assigned to
            them and they keep access until an admin reassigns{" "}
            {liveCases.length === 1 ? "it" : "each of them"}. Reassigning is a separate act, and
            it is what returns a case to the routing queue.
          </p>
          {/*
            Linked, because the case-detail screen exists: App.tsx routes
            /cases/:id to CaseDetail for every role, and the server scopes the row
            through authorize(case:read) — an admin reaches any case. Reassigning
            is the act this panel says has to happen next, and it happens there,
            so making the operator copy an id out of a <code> block was friction
            with nothing behind it.
          */}
          <ul>
            {liveCases.map((c) => (
              <li key={c.id}>
                <Link href={`/cases/${c.id}`}>
                  <code>{c.id}</code>
                </Link>{" "}
                — {c.status}, {c.registryDistrict ?? "registry district not yet resolved"}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="field">
        <label htmlFor={`reason-${profile.id}`}>Why is the platform doing this?</label>
        <select
          id={`reason-${profile.id}`}
          value={reason}
          onChange={(e) => setReason(e.target.value as VerificationRevocationReason)}
        >
          {VERIFICATION_REVOCATION_REASONS.map((code) => (
            <option key={code} value={code}>
              {REVOCATION_REASON_COPY[code]}
            </option>
          ))}
        </select>
        <small className="field-hint">{REVOCATION_REASON_CAVEAT[reason]}</small>
      </div>

      {revoke.error ? <p role="alert">{apiErrorText(revoke.error, "Could not record that.")}</p> : null}

      {/*
        Outlined red, not filled: this is an act an admin will feel as
        irreversible, and the panel above it is the thing meant to be read. A
        filled red block would be a dare.
      */}
      <div className="actions">
        <button
          type="button"
          className="danger"
          disabled={revoke.isPending}
          onClick={() => revoke.mutate()}
        >
          {revoke.isPending ? "Recording…" : "Stop routing work to them"}
        </button>
      </div>
    </div>
  );
}

/**
 * The reason codes in an operator's words.
 *
 * Keyed off VERIFICATION_REVOCATION_REASONS, so a code added to the shared
 * constant without copy here fails to compile rather than rendering a bare
 * SCREAMING_SNAKE token to an operator who then guesses what it means.
 *
 * The distinction these labels have to carry is the one the shared module spends
 * thirty lines on: four of these codes are facts about the professional's
 * ENROLMENT STANDING, and one is a fact about OUR OWN commercial choice. Picking
 * the wrong one is not a filing error — recording ENROLMENT_STRUCK_OFF because
 * clients complained would be the platform asserting a Supreme Court act that
 * never happened.
 */
const REVOCATION_REASON_COPY: Record<VerificationRevocationReason, string> = {
  ENROLMENT_STRUCK_OFF: "They have been struck off the roll",
  ENROLMENT_SUSPENDED: "Their enrolment is suspended",
  DETAILS_NO_LONGER_ACCURATE: "The details we verified are no longer accurate",
  AT_PROFESSIONALS_REQUEST: "They asked us to stop sending them work",
  WITHDRAWN_BY_PLATFORM: "This platform has decided to stop sending them work",
  OTHER: "Something else",
};

const REVOCATION_REASON_CAVEAT: Record<VerificationRevocationReason, string> = {
  ENROLMENT_STRUCK_OFF:
    "Record this ONLY if the Supreme Court has actually struck them off and you know it independently. It states a fact about their enrolment. If what you have is client reports, this is the wrong code — use the last one.",
  ENROLMENT_SUSPENDED:
    "Record this ONLY if their enrolment is genuinely suspended and you know it independently. It states a fact about their standing before the court, not about anything that happened here.",
  DETAILS_NO_LONGER_ACCURATE:
    "The enrolment number or registration we checked no longer matches. This is about our record being stale, and asserts nothing about them.",
  AT_PROFESSIONALS_REQUEST: "They asked. Nothing is being alleged and nothing is being decided.",
  WITHDRAWN_BY_PLATFORM:
    "This platform's own commercial decision to stop sending them cases. It asserts NOTHING about their enrolment and NOTHING about their conduct — it says only that we have chosen to stop. This is the correct code when your reason is client reports.",
  OTHER:
    "None of the above fits. Prefer a specific code where one is true: 'OTHER' records that a decision was made and almost nothing about why, and a year from now nobody will be able to reconstruct it. If your reason is client reports, the last code is the right one.",
};
